<template>
  <div id="about-page">
    <div class="about-amb" aria-hidden="true">
      <span class="orb o1"></span><span class="orb o2"></span>
    </div>

    <section class="hero">
      <span class="hero-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <path d="M3 8h11a3 3 0 1 0-3-3" /><path d="M3 12h15a3 3 0 1 1-3 3" /><path d="M3 16h9" />
        </svg>
      </span>
      <span class="eyebrow">하나의 계정, 여러 서비스</span>
      <h1 class="hero-title">일상에 필요한 도구들,<br /><span class="accent">breezer</span> 하나로.</h1>
      <p class="hero-desc">
        로또 분석부터 아파트 실거래가, 옷장 관리와 경조사 기록까지 —
        흩어져 있던 서비스를 계정 하나로 가볍게. 필요한 순간 바로 꺼내 쓰세요.
      </p>
      <div class="hero-cta">
        <a href="#services" class="btn-primary">서비스 둘러보기</a>
        <router-link to="/user/login" class="btn-ghost">로그인</router-link>
      </div>
    </section>

    <section id="services" class="services">
      <div class="sec-head">
        <h2 class="section-title">서비스</h2>
        <p>필요한 걸 골라 바로 들어가세요. 외부 서비스도 같은 계정으로 연결됩니다.</p>
      </div>
      <div class="service-grid">
        <component
          :is="card.external ? 'a' : 'router-link'"
          v-for="card in featureCards"
          :key="card.name"
          v-bind="card.external ? { href: card.path } : { to: card.path }"
          class="feature-card"
          :class="'acc-' + card.accent"
        >
          <div class="card-tile">{{ card.icon }}</div>
          <div class="card-title">
            {{ card.name }}<span v-if="card.external" class="ext">외부 ↗</span>
          </div>
          <div class="card-desc">{{ card.desc }}</div>
          <span class="card-go">
            바로가기
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          </span>
        </component>
      </div>
    </section>

    <section class="content-highlights">
      <div class="sec-head"><h2 class="section-title">주요 콘텐츠</h2></div>
      <div class="highlight-list">
        <router-link to="/apt/news" class="highlight-item">
          <span class="highlight-label">부동산 소식</span>
          <span class="highlight-text">매월 정리하는 부동산 시장 동향 리포트 &mdash; 금리 변화, 정책 이슈, 서울 주요 지역 시세 분석을 확인하세요.</span>
        </router-link>
        <router-link to="/apt/glossary" class="highlight-item">
          <span class="highlight-label">부동산 용어사전</span>
          <span class="highlight-text">전매제한, DTI, 재건축초과이익부담금 등 부동산 거래에 꼭 필요한 40여 개 핵심 용어를 카테고리별로 쉽게 찾아보세요.</span>
        </router-link>
        <router-link to="/lotto/result" class="highlight-item">
          <span class="highlight-label">로또 당첨 결과</span>
          <span class="highlight-text">최신 로또 회차별 당첨 번호와 1등~5등 당첨금 정보를 빠르게 확인하고, 번호 출현 빈도 통계를 분석해 보세요.</span>
        </router-link>
        <router-link to="/apt/trade" class="highlight-item">
          <span class="highlight-label">아파트 실거래가 조회</span>
          <span class="highlight-text">국토교통부 실거래가 공개 시스템 데이터를 기반으로 서울 25개 자치구의 아파트 매매 실거래가를 검색하고 시세 흐름을 파악하세요.</span>
        </router-link>
      </div>
    </section>

    <section class="about-intro">
      <p class="intro-text">
        Breezer는 일상에서 자주 필요하지만 흩어져 있던 생활 정보들을 한곳에 모아 제공하는 웹 서비스입니다.
        로또 당첨 번호 분석과 자동 번호 생성, 국토교통부 공공데이터 기반 서울 아파트 실거래가 조회,
        매월 정리하는 부동산 시장 동향 리포트, 부동산 용어사전 등 신뢰할 수 있는 데이터를 기반으로 유용한 정보를 제공합니다.
      </p>
      <p class="intro-text">
        또한 인스타그램 맞팔 확인, 경조사 내역 관리, 신체 치수 및 의류 사이즈 기록 등 개인 생활을 편리하게 관리할 수 있는 도구도 함께 제공합니다.
        모든 서비스는 무료로 이용 가능하며, 회원가입 없이도 대부분의 조회 기능을 사용할 수 있습니다.
      </p>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";

export default defineComponent({
  name: "AboutView",
  mixins: [UtilMixin],
  data() {
    return {
      featureCards: [
        {
          name: "로또", accent: "lotto", icon: "🎰", path: "/lotto/home",
          desc: "동행복권 로또 6/45 최신 당첨 결과 확인, 번호 출현 빈도·구간별 통계 분석. 자동 번호 생성과 구매 관리까지.",
        },
        {
          name: "부동산", accent: "apt", icon: "🏠", path: "/apt/home",
          desc: "국토교통부 데이터 기반 서울 25개 자치구 아파트 실거래가 조회. 관심 단지를 즐겨찾기에 등록하고 시세 변동을 추적하세요.",
        },
        {
          name: "인스타", accent: "insta", icon: "📱", path: "/insta/home",
          desc: "팔로잉·팔로워 데이터를 올리면 나를 팔로우하지 않는 계정을 찾아냅니다. 데이터는 브라우저에서만 처리됩니다.",
        },
        {
          name: "경조사", accent: "family", icon: "💍", path: "/family-event/home",
          desc: "결혼·장례·돌잔치 등 경조사 내역과 주고받은 금액을 한눈에 관리. 지인별·유형별 검색으로 과거 내역을 쉽게 찾아요.",
        },
        {
          name: "나의 옷장", accent: "closet", icon: "👔", path: "/closet/home",
          desc: "신체 치수를 기록하고 변화를 추적. 보유 의류의 브랜드·사이즈·실측을 등록해 온라인 쇼핑 시 맞는 사이즈를 빠르게.",
        },
        {
          name: "배드민턴", accent: "court", icon: "🏸", path: "/courtbreeze/", external: true,
          desc: "코트브리즈 — 급수·성별을 감안한 자동 대진표. 클럽을 만들어 회원과 게임 일정을 관리하세요.",
        },
        {
          name: "헬스", accent: "health", icon: "💪", path: "https://health.breezer.co.kr", external: true,
          desc: "헬스로그 — 운동·컨디션 기록과 통계. 같은 계정으로 이어서 이용하세요.",
        },
        {
          name: "급식", accent: "food", icon: "🍱", path: "/fooding/", external: true,
          desc: "라온 급식 — 오늘의 메뉴 확인과 주문·픽업. 팀 주문 현황도 한눈에.",
        },
      ],
    };
  },
});
</script>
